import { BaasicDropdownStore, BaseEditViewStore } from 'core/stores';
import { PatientNewFlowInsuranceEditModalForm } from 'application/facility-order-new-flow-insurance/forms';
import { InsuranceService } from 'application/import-reference/services';
import { PatientService } from "application/patient/services";
import { FacilityOrderTemplateService } from "application/facility-order/services";
import { ModalParams } from "core/models";
import { action, observable } from "mobx";
import { toDate } from "core/utils";

class PatientNewFlowInsuranceEditModalStore extends BaseEditViewStore {
    @observable accessor isOther = false;
    constructor(rootStore, componentProps) {
        const patientService = new PatientService(rootStore.application.baasic.apiClient);
        const insuranceService = new InsuranceService(rootStore.application.baasic.apiClient);

        const action = componentProps.action;
        const btPatient = componentProps.btPatient;
        const insurance = componentProps.insurance;

        super(rootStore, {
            name: 'facility-order-patient-new-flow-insurance-edit-modal',
            Form: PatientNewFlowInsuranceEditModalForm,
            id: action === "add" || action === "change" ? null : btPatient.id,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async () => {
                        let date = new Date(new Date(toDate(insurance.payorPolicyInfo.effectiveDate)).setHours(12, 0, 0, 0));
                        return { ...insurance.payorPolicyInfo, effectiveDate: date };
                    },
                    update: async (resource) => {
                        const response = await patientService.updatePatientInsuranceNewFlow(resource);
                        return response.data;
                    }
                }
            },
            FormClass: PatientNewFlowInsuranceEditModalForm
        });

        this.rootStore = rootStore;
        this.action = action;
        this.title = componentProps.title;
        this.patient = componentProps.patient;
        this.isPrimary = componentProps.isPrimary;
        this.isActive = componentProps.isActive;

        this.closeAll = componentProps.closeAll;
        this.refreshInsuranceData = componentProps.refreshInsuranceData;
        this.openRelationshipChangeModal = componentProps.openRelationshipChangeModal;
        this.changeFacilityOrderPatientInsuranceStatus = componentProps.changeFacilityOrderPatientInsuranceStatus;
        this.facilityOrderPatient = componentProps.facilityOrderPatient;
        this.fetchPatientTemplates = componentProps.fetchPatientTemplates;

        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);

        this.insuranceStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true,
        }, {
            fetchFunc: async (term) => {
                const response = await insuranceService.find({
                    searchQuery: term,
                    sort: this.isPrimary ? "primarySort|asc" : "secondarySort|asc",
                    rpp: 2500,
                    isPrimarySort: this.isPrimary
                });

                if (!this.isActive) {
                    if (this.isPrimary) {
                        this.form.$("name").set('value', 'Medicare')
                        return response.data.item.filter(x => x.name == "Medicare");
                    }
                    return response.data.item.filter(x => x.name.toLowerCase().includes("medicaid") && !x.name.toLowerCase().includes("do not use") && !x.name.toLowerCase().includes("(medicaid"));
                }

                return response.data.item;
            }
        });

        this.contactRelationshipStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ id: 1, name: 'None' }, { id: 2, name: 'Child' }, { id: 3, name: 'Friend' }, { id: 4, name: 'Grandparent' }, { id: 5, name: 'Other' }, { id: 6, name: 'Parent' }, { id: 7, name: 'Self' }, { id: 8, name: 'Spouse' }];
            }
        });

        if (!this.isActive)
            this.form.$("effectiveEndDate").set('rules', ['required']);

        if (action === "add" || action === "change") {
            this.form.$("name").set('rules', ['required']);
            this.form.$("policyNumber").set('rules', ['required']);
            this.form.$("effectiveDate").set('rules', ['required']);
        }

        this.insuranceChangeConfirmModal = new ModalParams({});
        this.insuranceChangeConfirmModal.yesLabel = 'Confirm';
        this.insuranceChangeConfirmModal.onCancel = () => { this.insuranceChangeConfirmModal.close() };
        this.insuranceChangeConfirmModal.noLabel = 'Cancel';
        this.insuranceChangeConfirmModal.swapButtons = true;
    }

    @action
    async initialize() {
        if (this.isEdit) {
            await this.getResource(this.id);
        }
        else {
            this.form.clear();
            if (this.action !== "edit") {
                this.form.$("contactRelationship").set('value', 'Self');
            }
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        let facilityOrderTemplates = (await this.facilityOrderTemplateService.find({
            facilityOrderId: this.facilityOrderPatient.facilityOrderId,
            patientId:  this.facilityOrderPatient.patientId,
            embed: "ClinicalStatus"
        })).data.item;

        if (this.isPrimary &&
            this.isActive &&
            this.facilityOrderPatient &&
            this.facilityOrderPatient.eligibilityStatus &&
            this.facilityOrderPatient.eligibilityStatus.name === "Eligible" &&
            facilityOrderTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
            this.insuranceChangeConfirmModal.onConfirm = async () => {
                this.insuranceChangeConfirmModal.loading = true;
                const result = await this.continueUpdateResource(resource);
                if (result) {
                    let updatePromises = facilityOrderTemplates.filter((x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required")))
                        .map(async template => {
                            template.clinicalStatusId = null;
                            template.clinicalStatus = null;
                            template.isClinicalReviewed = false;
                            template.isPriorAuthNeeded = false;
                            return this.facilityOrderTemplateService.updateNewFlowUSIntakeTemplate(template);
                        });
                    await Promise.all(updatePromises);

                    if (this.fetchPatientTemplates) {
                        await this.fetchPatientTemplates();
                    }
                }
                this.insuranceChangeConfirmModal.loading = false;
            };
            this.insuranceChangeConfirmModal.open({
                message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
            });
            return;
        }
        else {
            await this.continueUpdateResource(resource);
        }
    }

    @action.bound
    async continueUpdateResource(resource) {
        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }
            if (this.isPrimary) {
                await this.actions.update({
                    patientBrightreeId: this.patient.patientKey,
                    primaryInsuranceName: resource.name,
                    primaryInsurancePolicyNumber: resource.policyNumber,
                    primaryInsuranceStartDate: resource.effectiveDate,
                    isEdit: this.action === "edit",
                    isInactive: !this.isActive,
                    facilityOrderId: this.facilityOrderPatient.facilityOrderId,
                    patientId:  this.facilityOrderPatient.patientId,
                });
            } else {
                await this.actions.update({
                    patientBrightreeId: this.patient.patientKey,
                    secondaryInsuranceName: resource.name,
                    secondaryInsurancePolicyNumber: resource.policyNumber,
                    secondaryInsuranceStartDate: resource.effectiveDate,
                    isEdit: this.action === "edit",
                    isInactive: !this.isActive
                });
            }

            if (this.isPrimary && this.isActive && (this.action === "add" || this.action === "change") && this.changeFacilityOrderPatientInsuranceStatus) {
                await this.changeFacilityOrderPatientInsuranceStatus();
            }
            await this.refreshInsuranceData();
            this.form.setFieldsDisabled(false);
            await setTimeout(() => this.notifySuccessUpdate(this.name), 10);
            return true;
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            this.onUpdateError(err);
            return false;
        }
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.update) return;

        let facilityOrderTemplates = (await this.facilityOrderTemplateService.find({
            facilityOrderId: this.facilityOrderPatient.facilityOrderId,
            patientId:  this.facilityOrderPatient.patientId,
            embed: "ClinicalStatus"
        })).data.item;

        if (this.isPrimary &&
            this.isActive &&
            this.facilityOrderPatient &&
            this.facilityOrderPatient.eligibilityStatus &&
            this.facilityOrderPatient.eligibilityStatus.name === "Eligible" &&
            facilityOrderTemplates.some(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))) {
            this.insuranceChangeConfirmModal.onConfirm = async () => {
                this.insuranceChangeConfirmModal.loading = true;
                var result = await this.continueCreateResource(resource);

                if (result) {
                    let createPromises = facilityOrderTemplates.filter(x => x.clinicalStatus && (x.clinicalStatus.name === "Prior Auth Obtained" || x.clinicalStatus.name === "Prior Auth Required"))
                        .map(async template => {
                            template.clinicalStatusId = null;
                            template.clinicalStatus = null;
                            template.isClinicalReviewed = false;
                            template.isPriorAuthNeeded = false;
                            return this.facilityOrderTemplateService.updateNewFlowUSIntakeTemplate(template);
                        });
                    await Promise.all(createPromises);

                    if (this.fetchPatientTemplates)
                        await this.fetchPatientTemplates();
                }
                this.insuranceChangeConfirmModal.loading = false;
            };
            this.insuranceChangeConfirmModal.open({
                message: "After the patient's primary active insurance gets changed, the PARs will be removed from all of the patient’s templates."
            });
        }
        else {
            await this.continueCreateResource(resource);
        }
    }

    @action.bound
    async continueCreateResource(resource) {
        if (!this.isActive && resource.effectiveDate && resource.effectiveEndDate < resource.effectiveDate) {
            this.rootStore.notificationStore.error("End Date must be greater than Start Date.");
            return false;
        }

        this.form.setFieldsDisabled(true);

        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            if (this.isPrimary) {
                await this.actions.update({
                    patientBrightreeId: this.patient.patientKey,
                    primaryInsuranceName: resource.name,
                    primaryInsurancePolicyNumber: resource.policyNumber,
                    primaryInsuranceStartDate: resource.effectiveDate,
                    primaryInsuranceEndDate: !this.isActive ? resource.effectiveEndDate : undefined,
                    facilityOrderId: this.facilityOrderPatient.facilityOrderId,
                    patientId:  this.facilityOrderPatient.patientId,
                });
            } else {
                await this.actions.update({
                    patientBrightreeId: this.patient.patientKey,
                    secondaryInsuranceName: resource.name,
                    secondaryInsurancePolicyNumber: resource.policyNumber,
                    secondaryInsuranceStartDate: resource.effectiveDate,
                    secondaryInsuranceEndDate: !this.isActive ? resource.effectiveEndDate : undefined,
                });
            }

            if (this.isPrimary && this.isActive && (this.action === "add" || this.action === "change") && this.changeFacilityOrderPatientInsuranceStatus) {
                await this.changeFacilityOrderPatientInsuranceStatus();
            }

            await this.refreshInsuranceData();
            this.form.setFieldsDisabled(false);
            
            if (resource.contactRelationship !== "Self") {
                await this.openRelationshipChangeModal();
            }

            await setTimeout(() => this.notifySuccessCreate(), 10);
            return true;
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            this.onCreateError(err);
            return false;
        }
    }
}

export default PatientNewFlowInsuranceEditModalStore;
